import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, Wallet, TrendingUp, AlertTriangle, Clock, Target } from "lucide-react";
import KpiCard from "@/components/KpiCard";
import UsageTip from "@/components/UsageTip";
import ComisionesVendedorCard from "@/components/finanzas/ComisionesVendedorCard";
import SerieMensualBarras from "@/components/finanzas/SerieMensualBarras";

const fmt = (n) => `$ ${Math.round(Number(n) || 0).toLocaleString("es-CO")}`;
const hoy = new Date();
const pad = (n) => String(n).padStart(2, "0");
const mesActual = `${hoy.getFullYear()}-${pad(hoy.getMonth() + 1)}`;

function rangoDe(periodo, mes, anio) {
  if (periodo === "mes") {
    const [y, m] = mes.split("-").map(Number);
    const ult = new Date(y, m, 0).getDate();
    return { desde: `${mes}-01`, hasta: `${mes}-${pad(ult)}` };
  }
  if (periodo === "anio") return { desde: `${anio}-01-01`, hasta: `${anio}-12-31` };
  return {};
}

export default function FinanzasComisionesPage() {
  const [periodo, setPeriodo] = useState("mes");
  const [mes, setMes] = useState(mesActual);
  const [anio, setAnio] = useState(String(hoy.getFullYear()));
  const [vendedorId, setVendedorId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orden, setOrden] = useState("total_ventas");

  const rango = useMemo(() => rangoDe(periodo, mes, anio), [periodo, mes, anio]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (rango.desde) params.set("desde", rango.desde);
        if (rango.hasta) params.set("hasta", rango.hasta);
        if (vendedorId) params.set("vendedor_id", vendedorId);
        const { data } = await api.get(`/reportes/vendedores-analisis?${params}`);
        if (!cancel) setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No se pudo cargar el análisis de comisiones");
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [rango, vendedorId]);

  const vendedores = useMemo(() => {
    const list = [...(data?.vendedores || [])];
    list.sort((a, b) => (Number(b[orden]) || 0) - (Number(a[orden]) || 0));
    return list;
  }, [data, orden]);
  const t = data?.total || {};
  const etiquetaPeriodo = periodo === "mes" ? mes : periodo === "anio" ? anio : "Histórico completo";

  return (
    <div data-testid="finanzas-comisiones">
      <UsageTip id="finanzas-comisiones" title="Comisiones y comportamiento de vendedores" bullets={[
        "Filtra por <b>vendedor</b> y por <b>mes</b>, <b>año</b> o <b>todo el histórico</b>.",
        "<b>Causada</b> es la comisión generada; <b>por cobrar</b> ya la puedes liquidar (cliente pagó 100%); <b>bloqueada</b> espera el pago del cliente.",
        "Las tarjetas muestran disciplina de precios (% del sugerido, ítems bajo mínimo), cobro y cancelaciones por vendedor.",
      ]} />

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="comisiones-filtros">
        <div className="flex gap-1 bg-card border border-border rounded-lg p-1">
          {[["mes", "Mes"], ["anio", "Año"], ["todo", "Todo"]].map(([k, l]) => (
            <button key={k} onClick={() => setPeriodo(k)} data-testid={`comisiones-periodo-${k}`}
              className={`px-3 h-9 rounded-md text-sm font-medium transition-colors ${periodo === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}>{l}</button>
          ))}
        </div>
        {periodo === "mes" && (
          <input type="month" value={mes} onChange={(e) => e.target.value && setMes(e.target.value)} className="h-9 px-2 rounded-md border border-border bg-card text-sm" data-testid="comisiones-mes-input" />
        )}
        {periodo === "anio" && (
          <select value={anio} onChange={(e) => setAnio(e.target.value)} className="h-9 px-2 rounded-md border border-border bg-card text-sm" data-testid="comisiones-anio-select">
            {Array.from({ length: 6 }, (_, i) => String(hoy.getFullYear() - 3 + i)).map((y) => <option key={y}>{y}</option>)}
          </select>
        )}
        <select value={vendedorId} onChange={(e) => setVendedorId(e.target.value)} className="h-9 px-2 rounded-md border border-border bg-card text-sm min-w-[200px]" data-testid="comisiones-vendedor-select">
          <option value="">Todos los vendedores</option>
          {(data?.vendedores_lista || []).map((v) => <option key={v.id} value={v.id}>{v.nombre}</option>)}
        </select>
        <select value={orden} onChange={(e) => setOrden(e.target.value)} className="h-9 px-2 rounded-md border border-border bg-card text-sm ml-auto" data-testid="comisiones-orden-select">
          <option value="total_ventas">Ordenar: ventas</option>
          <option value="total_comision">Ordenar: comisión</option>
          <option value="pedidos">Ordenar: # pedidos</option>
          <option value="ticket_promedio">Ordenar: ticket promedio</option>
          <option value="pct_items_bajo_minimo">Ordenar: % bajo mínimo</option>
          <option value="tasa_cancelacion">Ordenar: % cancelación</option>
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4" data-testid="comisiones-kpis">
        <Kpi icon={TrendingUp} label={`Ventas · ${etiquetaPeriodo}`} to={`/productos?tab=historial${rango.desde ? `&desde=${rango.desde}&hasta=${rango.hasta}` : ""}`} value={fmt(t.total_ventas)} sub={`${t.pedidos || 0} pedidos`} testid="kpi-ventas" />
        <Kpi icon={Wallet} label="Comisión causada" to="/reglas?tab=comisiones" value={fmt(t.total_comision)} sub={t.total_ventas ? `${((t.total_comision / t.total_ventas) * 100).toFixed(1)}% efectivo` : "—"} testid="kpi-comision" />
        <Kpi icon={Target} label="Por cobrar (liquidar)" to="/reglas?tab=comisiones" value={fmt(t.comision_por_cobrar)} tone="emerald" testid="kpi-por-cobrar" />
        <Kpi icon={Clock} label="Bloqueada (cliente debe)" to="/pedidos?tab=pagos" value={fmt(t.comision_bloqueada)} tone="amber" testid="kpi-bloqueada" />
        <Kpi icon={Wallet} label="Ya liquidada" value={fmt(t.comision_liquidada)} tone="slate" testid="kpi-liquidada" />
        <Kpi icon={Users} label="Clientes atendidos" to="/clientes" value={t.clientes_unicos || 0} sub={`${t.cancelados || 0} cancelados`} testid="kpi-clientes" />
      </div>

      {data?.serie_mensual?.length > 1 && (
        <div className="bg-card border border-border rounded-xl p-4 mb-4" data-testid="comisiones-serie">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Ventas y comisión por mes</div>
          <SerieMensualBarras serie={data.serie_mensual} />
        </div>
      )}

      {loading && !data ? (
        <div className="text-sm text-muted-foreground py-10 text-center">Cargando…</div>
      ) : vendedores.length === 0 ? (
        <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border rounded-xl" data-testid="comisiones-vacio">Sin ventas en este periodo.</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3" data-testid="comisiones-vendedores">
          {vendedores.map((v) => <ComisionesVendedorCard key={v.vendedor_id || "sin"} v={v} fmt={fmt} />)}
        </div>
      )}
      {loading && data && <div className="text-[11px] text-muted-foreground mt-2">Actualizando…</div>}
      {!!(t.pedidos) && (
        <p className="text-[11px] text-muted-foreground mt-4 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Los % de cancelación excluyen borradores expirados automáticamente.</p>
      )}
    </div>
  );
}

function Kpi(props) { return <KpiCard {...props} />; }
